import { createAxiosServiceInstance } from '../../api';
import {
  getDataFromServer,
  XMLHttpRequestFunc,
  putDataToServer,
  postDataToServer,
  deleteDataFromServerWithFullResponse,
  throwIfDeleteFailed
} from '../../util';
import { convertFilterRangeParamsForBackend } from '../../../util/timezone';

const axios = createAxiosServiceInstance();

export class CommonModule {
  constructor(baseURL, httpHeaders) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
  }

  // FIN-10470: alert-history callers build from/to in display/browser space.
  // Convert to backend (stored-instant) space here — once per flow — so the
  // alert-history window matches the displayed day. No-op without timeAdjustment.
  fetchAMLSCommonUnifiedAlertsAPI = async (payload) => {
    const url = `${this.baseURL}/alerts`;
    return await getDataFromServer(
      url,
      this.httpHeaders,
      convertFilterRangeParamsForBackend(payload)
    );
  };

  fetchAMLSCommonUnifiedAlertCountsAPI = async (payload) => {
    const url = `${this.baseURL}/alerts/metrics`;
    return await getDataFromServer(url, this.httpHeaders, payload);
  };

  fetchAMLSCommonUnifiedAlertCountsByMetricsNameAPI = async (payload) => {
    const { metricName, data } = payload;
    const url = `${this.baseURL}/alerts/metrics/${metricName}`;
    return await getDataFromServer(url, this.httpHeaders, data);
  };

  fetchAMLSCommonUnifiedAlertsPreventionAPI = async (payload) => {
    const url = `${this.baseURL}/alerts/alertHistory`;
    // FIN-10470: same display-space → backend-space alignment as the alerts API.
    return await getDataFromServer(
      url,
      this.httpHeaders,
      convertFilterRangeParamsForBackend(payload)
    );
  };

  fetchAMLSCommonKYCAlertsAPI = async (payload) => {
    const { customerId = {} } = payload;
    const url = `${this.baseURL}/customers/${customerId}/similarKyc`;
    return await getDataFromServer(url, this.httpHeaders, payload);
  };

  // FIN-13724 — `params` is optional ({ alertId } from the Spatial alert view);
  // it routes the read to the alert read scope via the `?alertId=` alias in
  // CommonAaaResourcesHandler, as fetchAmlsAlertLevelList does.
  fetchTypologiesActionsAPI = async (params) => {
    const url = `${this.baseURL}/fraudactions`;
    return params
      ? await getDataFromServer(url, this.httpHeaders, params)
      : await getDataFromServer(url, this.httpHeaders);
  };

  fetchAMLSCommonKYCDownloadAlertsAPI = async (payload) => {
    const { customerId = {} } = payload;
    const url = `${this.baseURL}/customers/${customerId}/similarKyc`;
    return await getDataFromServer(url, this.httpHeaders, payload);
  };

  fetchAMLSCommonAlertDetailsAPI = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonAlertPartyIdentityAPI = async (payload) => {
    const { alertId, isList, ...params } = payload;
    let url = `${this.baseURL}/alerts/${alertId}/customer`;
    if (isList) {
      url = `${this.baseURL}/alerts/${alertId}/customerlist`;
    }

    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonAlertAssocCustomersAPI = async (payload) => {
    const { customerId, ...params } = payload;
    const url = `${this.baseURL}/customers/${customerId}/linkedCustomers`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonGetTicketsAPI = async (payload) => {
    const { sourceId, offset, limit } = payload;
    const params = {
      offset,
      limit
    };
    const url = `${this.baseURL}/tickets/all/${sourceId}`;
    return getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonGetTicketDetailsAPI = async (payload) => {
    const { ticketId } = payload;
    const url = `${this.baseURL}/tickets/${ticketId}`;
    return getDataFromServer(url, this.httpHeaders);
  };

  fetchAMLSCommonApproveAndRejectTicketAPI = async (payload) => {
    let url = `${this.baseURL}/tickets/${payload.ticketId}?status=${payload.status}`;
    if (payload?.status === 'Rejected') {
      url += `&reason=${payload.reason}`;
    }
    return await putDataToServer(url, this.httpHeaders, {}, {});
  };

  updateAMLSCommonAlertPartyAPI = async (payload) => {
    const url = `${this.baseURL}/alerts/bulkUpdate`;
    return await putDataToServer(url, this.httpHeaders, undefined, payload);
  };

  fetchAMLSCommonSandboxUI = async (payload) => {
    const url = `http://13.0.0.132:9200/sandbox_watchlist_new/_search`;
    const dataStringify = JSON.stringify(payload);
    return await XMLHttpRequestFunc(url, dataStringify);
  };

  fetchAMLSCommonGetDynamicPropertiesCategoriesAPI = async (payload) => {
    const url = `${this.baseURL}/dyn-prop/categories`;
    const params = { ...payload };
    return getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonGetDynamicPropertiesAPI = async (payload) => {
    const url = `${this.baseURL}/dyn-prop/`;
    const params = { ...payload };
    return getDataFromServer(url, this.httpHeaders, params);
  };

  updateAMLSCommonPutDynamicPropertiesAPI = async (moduleId, payload) => {
    const url = `${this.baseURL}/dyn-prop/value`;
    const data = { ...payload };
    return putDataToServer(
      url,
      this.httpHeaders,
      { params: { moduleId } },
      data
    );
  };

  fetchAMLSCommonGetPipelinesAPI = async (payload) => {
    const url = `${this.baseURL}/pipelines`;
    const params = { ...payload };
    return getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonPostPipelinesRunAPI = async ({ id, moduleId }) => {
    const url = `${this.baseURL}/pipelines/${id}/execute?moduleId=${moduleId}`;
    return postDataToServer(url, this.httpHeaders);
  };

  postAMLSCommonPostPipelinesScheduleAPI = async (payload) => {
    const data = payload?.data;
    const id = payload?.id;
    const moduleId = payload?.moduleId;
    const url = `${this.baseURL}/pipelines/schedule/${id}?moduleId=${moduleId}`;
    return postDataToServer(url, data);
  };

  postAMLSCommonPostPipelinesUnScheduleAPI = async (payload) => {
    const id = payload?.id;
    const moduleId = payload?.moduleId;
    const url = `${this.baseURL}/pipelines/unschedule/${id}?moduleId=${moduleId}`;
    return postDataToServer(url, id);
  };

  fetchAMLSCommonGetPipelinesScheduleInfoAPI = async (payload) => {
    const id = payload?.id;
    const moduleId = payload?.moduleId;
    const url = `${this.baseURL}/pipelines/schedule/${id}?moduleId=${moduleId}`;
    return getDataFromServer(url, this.httpHeaders);
  };

  fetchAMLSCommonGetPipelinesHistoryAPI = async (id, payload) => {
    const url = `${this.baseURL}/pipelines/${id}/executions`;
    return getDataFromServer(url, this.httpHeaders, payload);
  };

  postAMLSCommonPostPipelineStopAPI = async (payload) => {
    const id = payload?.id;
    const moduleId = payload?.moduleId;
    const url = `${this.baseURL}/pipelines/${id}/stop?moduleId=${moduleId}`;
    return postDataToServer(url, id);
  };

  fetchBlackList = async () => {
    const url = `${this.baseURL}/blacklists`;
    return await getDataFromServer(url, this.httpHeaders, {});
  };

  fetchAMLSCommonGetNotificationsAPI = async (params) => {
    const url = `${this.baseURL}/notifications`;
    return getDataFromServer(url, this.httpHeaders, params);
  };

  fetchAMLSCommonQueryFieldsAPI = async () => {
    const url = `${this.baseURL}/customfields/fields?enabled=true`;

    return axios.get(url).then((res) => {
      if (res.status !== 200) {
        throw new Error('Failed to fetch query fields');
      }

      return res.data;
    });
  };

  fetchAMLSCommonDropdownOptionsAPI = async () => {
    // FIN-12351 — `grouping_key_pairs` is the curated set of legal grouping-key value
    // redirections (Perspective / Swap). The endpoint takes an arbitrary comma-separated
    // category list and has no server-side allowlist, so adding one is a client-side change.
    // A deployment whose `v6.4.1_grouping_key_pairs.sql` has not been applied simply returns
    // no row for it, and the drawer falls back to offering no perspective control.
    const url = `${this.baseURL}/metadata/dropdowns?categories=formula_function,aggregation_function,screening_function,tooltips,grouping_key_pairs`;

    return axios.get(url).then((res) => {
      if (res.status !== 200) {
        throw new Error('Failed to fetch dropdown options');
      }

      return res.data;
    });
  };

  // FIN-13569 — `params` is optional ({ alertId } from the Spatial alert view
  // only); without it the request is unchanged. See the `?alertId=` alias in
  // CommonAaaResourcesHandler. The endpoint ignores the parameter.
  fetchAmlsAlertLevelList = async (params) => {
    const url = `${this.baseURL}/alertLevelClassification`;
    return params
      ? getDataFromServer(url, this.httpHeaders, params)
      : getDataFromServer(url, this.httpHeaders);
  };

  fetchAmlsExportHistory = async (params) => {
    const url = `${this.baseURL}/downloads`;
    return getDataFromServer(url, this.httpHeaders, params);
  };

  fetchDownloadUrl = async (jobId) => {
    const url = `${this.baseURL}/downloads/${jobId}/url`;
    return getDataFromServer(url, this.httpHeaders);
  };

  // FIN-12858 — a failed delete used to report success. `deleteDataFromServer` returns the
  // BODY, and the axios instance runs with `validateStatus: false`, so a 403/404/400 resolved
  // like a 202: the component awaited it, showed "Export deleted", refetched, and the row was
  // still there. The backend answers 202 on success and 400/403/404 each carrying a `msg`
  // (AsyncDownloadController), so keep the full response and surface that reason — the
  // component's FIN-11542 catch was already waiting for it and could never fire.
  //
  // `this.httpHeaders` was also being passed as the second argument, which is the request
  // BODY, not headers. Auth comes from the instance's request interceptor, so it changed
  // nothing, but it sent the header map as a JSON body on every delete.
  deleteAmlsExportHistory = async (jobId) => {
    const url = `${this.baseURL}/downloads/${jobId}`;
    return throwIfDeleteFailed(
      await deleteDataFromServerWithFullResponse(url),
      'Failed to delete export. Please try again.'
    );
  };
}
